import { addMinutes, isWithinInterval, parseISO, subMinutes } from 'date-fns';
import { Lessons } from 'screens/CoachDetails/types';

export const mergeDateAndSlot = (date: Date, slot: string): Date => {
  const [time, modifier] = slot.split(' ');
  let [hours, minutes] = time.split(':').map(Number);

  if (modifier.toLowerCase() === 'pm' && hours < 12) {
    hours += 12;
  } else if (modifier.toLowerCase() === 'am' && hours === 12) {
    hours = 0;
  }

  const mergedDate = new Date(date);
  mergedDate.setHours(hours, minutes, 0, 0);

  return mergedDate;
};

export const isSlotOverlapping = (mergedDate: Date, bookedArray: Lessons): boolean => {
  return bookedArray?.some((booked) => {
    const start = parseISO(booked.startDateTime);
    const end = addMinutes(start, 60);
    return isWithinInterval(mergedDate, {
      start: subMinutes(start, 59),
      end: subMinutes(end, 1),
    });
  });
};

// function to check if a slot is exactly booked (not just overlapping)
export const isSlotExactlyBooked = (mergedDate: Date, bookedArray: Lessons): boolean => {
  return bookedArray?.some((booked) => {
    const bookedStart = parseISO(booked.startDateTime);
    return bookedStart.getTime() === mergedDate.getTime();
  });
};

// Check if slot overlaps with Google Calendar events
export const isSlotOverlappingWithGoogle = (mergedDate: Date, googleEvents: any[]): boolean => {
  if (!googleEvents || googleEvents.length === 0) return false;

  const slotStartDate = mergedDate;
  const slotEndDate = new Date(slotStartDate.getTime() + 60 * 60 * 1000);

  return googleEvents.some((event: any) => {
    if (!event.start?.dateTime || !event.end?.dateTime) return false;

    const eventStart = new Date(event.start.dateTime);
    const eventEnd = new Date(event.end.dateTime);

    return (
      (slotStartDate >= eventStart && slotStartDate < eventEnd) ||
      (slotEndDate > eventStart && slotEndDate <= eventEnd) ||
      (slotStartDate <= eventStart && slotEndDate >= eventEnd)
    );
  });
};

// Check if a slot is exactly booked in Google Calendar
export const isSlotExactlyBookedInGoogle = (mergedDate: Date, googleEvents: any[]): boolean => {
  if (!googleEvents || googleEvents.length === 0) return false;

  return googleEvents.some((event: any) => {
    if (!event.start?.dateTime) return false;
    const eventStart = new Date(event.start.dateTime);
    return eventStart.getTime() === mergedDate.getTime();
  });
};

// Updated function to filter slots and handle both in-app and Google Calendar booking conflicts
export const filterSlotsWithBookingLogic = (
  slots: { time: string; venue: any }[],
  date: Date,
  bookedArray: Lessons,
  googleEvents: any[] = [],
): { time: string; venue: any; isBooked: boolean; hasOverlap: boolean }[] => {
  return slots
    .map((slot) => {
      const mergedDate = mergeDateAndSlot(date, slot.time);
      const isExactlyBookedInApp = isSlotExactlyBooked(mergedDate, bookedArray);
      const isExactlyBookedInGoogle = isSlotExactlyBookedInGoogle(mergedDate, googleEvents);
      const hasOverlapInApp = isSlotOverlapping(mergedDate, bookedArray);
      const hasOverlapInGoogle = isSlotOverlappingWithGoogle(mergedDate, googleEvents);

      return {
        ...slot,
        isBooked: isExactlyBookedInApp || isExactlyBookedInGoogle,
        hasOverlap: hasOverlapInApp || hasOverlapInGoogle,
      };
    })
    .filter((slot) => {
      // Keep the slot if:
      // 1. It has no overlap with bookings (both in-app and Google), OR
      // 2. It's exactly the booked slot (we want to show this as "booked")
      return !slot.hasOverlap || slot.isBooked;
    });
};
